import React, { useContext, useRef, useState, useEffect, useMemo } from "react";
import { filterContext } from "../../stores/filters/filterContext";
import { userContext } from "../../stores/users/userContext";

const VIEW = "Super Transcript";

const SPEAKER_COLORS = ["#e53935", "#3b82f6", "#22c55e", "#f59e0b", "#a855f7", "#06b6d4", "#f97316"];

function formatTime(seconds) {
  if (!seconds && seconds !== 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function SuperTranscriptView({ sentences, videoFile }) {
  const filterStore = useContext(filterContext);
  const userStore = useContext(userContext);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const sentenceRefs = useRef({});
  const isDragging = useRef(false);
  const colorMap = useRef(new Map());

  // highlight: color sentences. filterMode: hide non-highlighted sentences.
  const [highlights, setHighlights] = useState({ positive: false, negative: false, question: false });
  const [filterMode, setFilterMode] = useState(false);
  const [currentTimeSec, setCurrentTimeSec] = useState(0);
  const [rightWidth, setRightWidth] = useState(440);
  const [searchText, setSearchText] = useState("");
  const [autoScroll, setAutoScroll] = useState(true);

  const anyHighlightOn = highlights.positive || highlights.negative || highlights.question;

  const selectedIds = useMemo(
    () => new Set(filterStore.getSelectedSentences(VIEW) || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterStore.getSelectedSentences(VIEW)]
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoFile) return;
    const onTime = () => setCurrentTimeSec(video.currentTime);
    video.addEventListener("timeupdate", onTime);
    return () => video.removeEventListener("timeupdate", onTime);
  }, [videoFile]);

  // Auto-scroll to playing sentence
  useEffect(() => {
    if (!autoScroll) return;
    const playing = sorted.find(
      (s) => currentTimeSec >= (s.start_in || 0) && currentTimeSec < (s.end_in || Infinity)
    );
    if (playing && sentenceRefs.current[playing.id]) {
      sentenceRefs.current[playing.id].scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTimeSec, autoScroll]);

  // Resizable divider — drag right panel as wide as you want (min 260px, transcript keeps 180px)
  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newW = rect.right - e.clientX;
      const maxW = rect.width - 180; // keep at least 180px for transcript
      setRightWidth(Math.max(260, Math.min(maxW, newW)));
    };
    const onUp = () => {
      isDragging.current = false;
      document.body.style.cursor = "";
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  const seekTo = (seconds) => {
    if (videoRef.current) videoRef.current.currentTime = seconds;
    const main = userStore.getVideo();
    if (main) main.currentTime = seconds;
  };

  const toggleSelect = (e, sentence) => {
    e.stopPropagation();
    const id = sentence.id;
    if (selectedIds.has(id)) {
      filterStore.removeSelectedSentence(id, VIEW);
    } else {
      filterStore.setSelectedSentences(id, VIEW);
    }
  };

  const sorted = useMemo(
    () => [...sentences].filter((s) => !s.space).sort((a, b) => (a.start_in || 0) - (b.start_in || 0)),
    [sentences]
  );

  // Group consecutive sentences by speaker
  const grouped = useMemo(() => {
    colorMap.current = new Map();
    let colorIdx = 0;
    const groups = [];
    let cur = null;
    for (const s of sorted) {
      const speaker = s.speaker || "";
      if (!colorMap.current.has(speaker) && speaker) {
        colorMap.current.set(speaker, SPEAKER_COLORS[colorIdx++ % SPEAKER_COLORS.length]);
      }
      if (!cur || speaker !== cur.speaker) {
        cur = { speaker, color: colorMap.current.get(speaker) || null, sentences: [s] };
        groups.push(cur);
      } else {
        cur.sentences.push(s);
      }
    }
    return groups;
  }, [sorted]);

  const selectedSentences = sorted.filter((s) => selectedIds.has(s.id));

  // Determine if a sentence matches active highlights
  const isHighlighted = (s) => {
    if (highlights.question && s.question) return true;
    if (highlights.positive && s.sentiment === "positive") return true;
    if (highlights.negative && s.sentiment === "negative") return true;
    return false;
  };

  const getHighlightColor = (s) => {
    if (highlights.question && s.question) return { bg: "rgba(59,130,246,0.22)", border: "#3b82f6", label: "?" };
    if (highlights.positive && s.sentiment === "positive") return { bg: "rgba(34,197,94,0.22)", border: "#22c55e", label: "+" };
    if (highlights.negative && s.sentiment === "negative") return { bg: "rgba(239,68,68,0.22)", border: "#ef4444", label: "−" };
    return null;
  };

  const matchSearch = (text) =>
    !searchText.trim() || (text || "").toLowerCase().includes(searchText.toLowerCase());

  const renderText = (text) => {
    if (!searchText.trim()) return text;
    const lower = (text || "").toLowerCase();
    const idx = lower.indexOf(searchText.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark style={{ background: "#e53935", color: "#fff", borderRadius: "2px", padding: "0 1px" }}>
          {text.slice(idx, idx + searchText.length)}
        </mark>
        {text.slice(idx + searchText.length)}
      </>
    );
  };

  return (
    <div
      ref={containerRef}
      style={{ display: "flex", height: "100%", minHeight: 0, fontFamily: "Outfit, sans-serif", overflow: "hidden" }}
    >
      {/* ── LEFT: Transcript ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, background: "#141414", overflow: "hidden" }}>

        {/* Toolbar row */}
        <div style={{
          display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap",
          padding: "9px 14px", borderBottom: "1px solid #222", flexShrink: 0,
          background: "#111",
        }}>
          {/* Search */}
          <div style={{ position: "relative", flex: "1 1 150px", minWidth: "100px" }}>
            <span style={{ position: "absolute", left: "8px", top: "50%", transform: "translateY(-50%)", color: "#555", fontSize: "13px", pointerEvents: "none" }}>⌕</span>
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search transcript…"
              style={{
                width: "100%", background: "#1c1c1c", border: "1px solid #2a2a2a",
                borderRadius: "7px", padding: "5px 10px 5px 26px", color: "#ccc",
                fontSize: "12px", outline: "none", fontFamily: "Outfit, sans-serif",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ width: "1px", height: "20px", background: "#2a2a2a", flexShrink: 0 }} />

          {/* Highlight toggles */}
          <span style={{ fontSize: "10px", color: "#555", flexShrink: 0 }}>Highlight:</span>
          {[
            { key: "positive", label: "Positive", color: "#22c55e" },
            { key: "negative", label: "Negative", color: "#ef4444" },
            { key: "question", label: "Questions", color: "#3b82f6" },
          ].map(({ key, label, color }) => (
            <button
              key={key}
              onClick={() => {
                setHighlights((p) => {
                  const next = { ...p, [key]: !p[key] };
                  // Turn off filterMode if no highlights remain
                  if (!next.positive && !next.negative && !next.question) setFilterMode(false);
                  return next;
                });
              }}
              style={{
                background: highlights[key] ? color : "transparent",
                color: highlights[key] ? "#fff" : "#666",
                border: `1px solid ${highlights[key] ? color : "#2a2a2a"}`,
                borderRadius: "20px", padding: "3px 10px",
                fontFamily: "Outfit, sans-serif", fontSize: "11px", fontWeight: 600,
                cursor: "pointer", transition: "all 0.15s", flexShrink: 0,
              }}
            >{label}</button>
          ))}

          {/* Filter-to-highlighted — only shown when a highlight is active */}
          {anyHighlightOn && (
            <>
              <div style={{ width: "1px", height: "20px", background: "#2a2a2a", flexShrink: 0 }} />
              <button
                onClick={() => setFilterMode((v) => !v)}
                title="Show only highlighted sentences — hide everything else"
                style={{
                  background: filterMode ? "rgba(255,200,0,0.15)" : "transparent",
                  color: filterMode ? "#fbbf24" : "#666",
                  border: `1px solid ${filterMode ? "#fbbf24" : "#2a2a2a"}`,
                  borderRadius: "20px", padding: "3px 11px",
                  fontFamily: "Outfit, sans-serif", fontSize: "11px", fontWeight: 600,
                  cursor: "pointer", transition: "all 0.15s", flexShrink: 0,
                  display: "flex", alignItems: "center", gap: "4px",
                }}
              >
                <span style={{ fontSize: "10px" }}>▼</span>
                {filterMode ? "Showing only highlighted" : "Filter to highlighted"}
              </button>
            </>
          )}

          <div style={{ marginLeft: "auto", display: "flex", gap: "6px", alignItems: "center", flexShrink: 0 }}>
            {/* Auto-scroll */}
            <button
              onClick={() => setAutoScroll((v) => !v)}
              title="When ON, the transcript scrolls to follow the playing sentence"
              style={{
                background: autoScroll ? "rgba(229,57,53,0.12)" : "transparent",
                color: autoScroll ? "#e53935" : "#555",
                border: `1px solid ${autoScroll ? "rgba(229,57,53,0.35)" : "#2a2a2a"}`,
                borderRadius: "20px", padding: "3px 10px",
                fontFamily: "Outfit, sans-serif", fontSize: "11px", cursor: "pointer",
              }}
            >{autoScroll ? "⟳ Auto-scroll ON" : "⟳ Auto-scroll OFF"}</button>

            <span style={{ fontSize: "10px", color: "#393939" }}>
              ○ = basket · click text = seek
            </span>
          </div>
        </div>

        {/* Scrollable transcript */}
        <div style={{ overflowY: "auto", flex: 1, padding: "24px 32px 48px" }}>
          {grouped.map((group, gi) => {
            // Filter groups: if filterMode, skip groups with no highlighted sentences
            const visibleSentences = group.sentences.filter((s) => {
              if (searchText.trim() && !matchSearch(s.sentence)) return false;
              if (filterMode && anyHighlightOn && !isHighlighted(s)) return false;
              return true;
            });
            if (visibleSentences.length === 0) return null;

            return (
              <div key={gi} style={{ marginBottom: "28px" }}>
                {/* Speaker label */}
                {group.speaker && (
                  <div style={{
                    fontSize: "10px", fontWeight: 700, letterSpacing: "0.1em",
                    textTransform: "uppercase", color: group.color || "#666",
                    marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px",
                  }}>
                    <span style={{
                      width: "8px", height: "8px", borderRadius: "50%",
                      background: group.color || "#666", display: "inline-block",
                    }} />
                    {group.speaker}
                  </div>
                )}

                {/* Sentences as inline paragraph */}
                <div style={{ lineHeight: 2.1, letterSpacing: "0.01em" }}>
                  {visibleSentences.map((s) => {
                    const isPlaying = currentTimeSec >= (s.start_in || 0) && currentTimeSec < (s.end_in || Infinity);
                    const isSelected = selectedIds.has(s.id);
                    const hl = getHighlightColor(s);

                    let bg = "transparent";
                    let underlineColor = "transparent";
                    if (isPlaying) { bg = "rgba(229,57,53,0.28)"; underlineColor = "#e53935"; }
                    else if (isSelected) { bg = "rgba(229,57,53,0.14)"; underlineColor = "#e53935"; }
                    else if (hl) { bg = hl.bg; underlineColor = hl.border; }

                    return (
                      <span
                        key={s.id}
                        ref={(el) => { sentenceRefs.current[s.id] = el; }}
                        style={{ display: "inline" }}
                      >
                        {/* Basket checkbox — larger hit target */}
                        <span
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => toggleSelect(e, s)}
                          title={isSelected ? "Remove from basket" : "Add to basket"}
                          style={{
                            display: "inline-block",
                            width: "16px", height: "16px",
                            borderRadius: "50%",
                            border: `2px solid ${isSelected ? "#e53935" : "#3a3a3a"}`,
                            background: isSelected ? "#e53935" : "transparent",
                            marginRight: "4px", marginLeft: "4px",
                            verticalAlign: "middle", cursor: "pointer",
                            fontSize: "8px", color: "#fff", fontWeight: 700,
                            textAlign: "center", lineHeight: "12px",
                            transition: "all 0.15s", flexShrink: 0,
                            position: "relative", zIndex: 2,
                            userSelect: "none",
                          }}
                        >{isSelected ? "✓" : ""}</span>

                        {/* Sentence text — click seeks */}
                        <span
                          onClick={() => seekTo(s.start_in || 0)}
                          title={`${formatTime(s.start_in)} — click to play from here`}
                          style={{
                            display: "inline",
                            cursor: "pointer",
                            borderRadius: "3px",
                            padding: "1px 3px",
                            background: bg,
                            borderBottom: `2px solid ${underlineColor}`,
                            color: isPlaying ? "#fff" : "#ccc",
                            fontSize: "14px",
                            transition: "background 0.1s",
                          }}
                        >
                          <span style={{ fontSize: "9px", color: "#3e3e3e", marginRight: "3px", verticalAlign: "middle", fontVariantNumeric: "tabular-nums" }}>
                            {formatTime(s.start_in)}
                          </span>
                          {renderText(s.sentence || "")}
                        </span>
                        {"  "}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Drag handle — grab this to resize video panel ── */}
      <div
        onMouseDown={() => { isDragging.current = true; document.body.style.cursor = "col-resize"; }}
        title="Drag to resize video panel"
        style={{
          width: "6px", flexShrink: 0, cursor: "col-resize",
          background: "#1a1a1a",
          borderLeft: "1px solid #222", borderRight: "1px solid #222",
          transition: "background 0.15s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(229,57,53,0.35)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "#1a1a1a"; }}
      />

      {/* ── RIGHT: Video + Basket ── */}
      <div style={{
        width: `${rightWidth}px`, flexShrink: 0,
        display: "flex", flexDirection: "column", background: "#111", overflow: "hidden",
      }}>
        {/* Video player */}
        <div style={{ background: "#000", flexShrink: 0 }}>
          {videoFile ? (
            <video
              ref={videoRef}
              src={videoFile}
              controls
              style={{ width: "100%", display: "block", maxHeight: "60vh" }}
            />
          ) : (
            <div style={{ height: "220px", display: "flex", alignItems: "center", justifyContent: "center", color: "#444", fontSize: "13px" }}>
              No video loaded
            </div>
          )}
        </div>

        {/* Basket */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "10px", gap: "8px" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#e53935" }}>
              Basket ({selectedSentences.length})
            </span>
            {selectedSentences.length > 0 && (
              <>
                <span style={{ fontSize: "10px", color: "#444", flex: 1 }}>
                  {selectedSentences.reduce((t, s) => t + ((s.end_in || 0) - (s.start_in || 0)), 0).toFixed(1)}s total
                </span>
                <button
                  onClick={() => selectedSentences.forEach((s) => filterStore.removeSelectedSentence(s.id, VIEW))}
                  style={{ background: "transparent", border: "none", color: "#555", cursor: "pointer", fontSize: "11px" }}
                >Clear all</button>
              </>
            )}
          </div>

          {selectedSentences.length === 0 ? (
            <p style={{ fontSize: "12px", color: "#444", margin: 0, lineHeight: 1.7 }}>
              Click the ○ circle next to any sentence to add it to your export basket.
            </p>
          ) : (
            selectedSentences.map((s) => (
              <div
                key={s.id}
                style={{
                  background: "#1a1a1a", borderRadius: "8px", padding: "8px 10px",
                  marginBottom: "6px", display: "flex", gap: "8px", alignItems: "flex-start",
                  border: "1px solid #252525",
                }}
              >
                <button
                  onClick={() => seekTo(s.start_in || 0)}
                  style={{
                    background: "#e53935", color: "#fff", border: "none", borderRadius: "50%",
                    width: "22px", height: "22px", flexShrink: 0, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "9px",
                  }}
                  title="Seek to this clip"
                >▶</button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "10px", color: "#555", marginBottom: "2px", display: "flex", gap: "6px" }}>
                    <span>{formatTime(s.start_in)} – {formatTime(s.end_in)}</span>
                    {s.speaker && (
                      <span style={{ color: colorMap.current.get(s.speaker) || "#666" }}>{s.speaker}</span>
                    )}
                  </div>
                  <div style={{
                    fontSize: "12px", color: "#bbb", lineHeight: 1.4,
                    overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
                    WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  }}>
                    {s.sentence}
                  </div>
                </div>
                <button
                  onClick={(e) => toggleSelect(e, s)}
                  style={{ background: "transparent", border: "none", color: "#555", cursor: "pointer", fontSize: "16px", lineHeight: 1, flexShrink: 0 }}
                  title="Remove"
                >×</button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
